import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { AlertCircle, ExternalLink, Loader2, Plus, RefreshCw, Search, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { articlesApi, uploadArticleCoverImage } from "@/lib/api/articles";
import { resolveNavImagePreviewUrl } from "@/lib/api/navImageUpload";
import {
  formatAdminBusinessEt,
  nowZonedDatetimeLocalInput,
  utcIsoToZonedDatetimeLocalInput,
  zonedDatetimeLocalInputToUtcIso,
} from "@/lib/admin-datetime";
import { DEFAULT_SCHEDULE_TIME_ZONE } from "@/lib/schedule-timezone";
import type { Article } from "@/types/article";

type EditorState = {
  open: boolean;
  mode: "create" | "edit";
  articleId?: string;
  title: string;
  slug: string;
  locale: string;
  bodyHtml: string;
  excerpt: string;
  coverImageUrl: string;
  metaTitle: string;
  metaDescription: string;
  primaryKeyword: string;
  published: boolean;
  /** `datetime-local` wall clock in US Eastern */
  publishedAtLocal: string;
  source?: Article["source"];
  url?: string;
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 200);
}

function formatApiError(e: unknown): string {
  if (!(e instanceof Error)) return "Request failed";
  const body = (
    e as {
      responseBody?: { details?: { fieldErrors?: Record<string, string | string[]> }; message?: string };
    }
  ).responseBody;
  const fe = body?.details?.fieldErrors;
  if (fe && typeof fe === "object") {
    const parts = Object.entries(fe).flatMap(([k, v]) =>
      Array.isArray(v) ? v.map((x) => `${k}: ${x}`) : [`${k}: ${v}`]
    );
    if (parts.length) return parts.join(" · ");
  }
  return body?.message || e.message;
}

const emptyEditor = (): EditorState => ({
  open: false,
  mode: "create",
  title: "",
  slug: "",
  locale: "en",
  bodyHtml: "",
  excerpt: "",
  coverImageUrl: "",
  metaTitle: "",
  metaDescription: "",
  primaryKeyword: "",
  published: false,
  publishedAtLocal: nowZonedDatetimeLocalInput(DEFAULT_SCHEDULE_TIME_ZONE),
});

export default function ArticlesPage() {
  const navigate = useNavigate();
  const { articleId: routeArticleId } = useParams<{ articleId?: string }>();

  const [items, setItems] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [publishedFilter, setPublishedFilter] = useState<"all" | "true" | "false">("all");
  const [sourceFilter, setSourceFilter] = useState<"all" | "admin" | "brand_pilot">("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  const [deleteTarget, setDeleteTarget] = useState<Article | null>(null);
  const [editor, setEditor] = useState<EditorState>(emptyEditor());
  const [slugTouched, setSlugTouched] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  /** Bypass WYSIWYG paste — paste full HTML here for blog articles. */
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const coverFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const load = useCallback(
    async (opts?: { soft?: boolean }) => {
      try {
        if (opts?.soft) setRefreshing(true);
        else setLoading(true);
        setError(null);
        const res = await articlesApi.list({
          page,
          limit,
          search: debouncedSearch || undefined,
          published: publishedFilter === "all" ? undefined : publishedFilter === "true",
          source: sourceFilter === "all" ? undefined : sourceFilter,
        });
        setItems(res.data?.articles || []);
        setTotal(res.data?.pagination?.total || 0);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, limit, debouncedSearch, publishedFilter, sourceFilter]
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, publishedFilter, sourceFilter]);

  useEffect(() => {
    if (!routeArticleId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await articlesApi.get(routeArticleId);
        if (cancelled || !res.data?.article) return;
        const a = res.data.article;
        setEditor({
          open: true,
          mode: "edit",
          articleId: a._id,
          title: a.title,
          slug: a.slug,
          locale: a.locale || "en",
          bodyHtml: a.bodyHtml || "",
          excerpt: a.excerpt || "",
          coverImageUrl: a.coverImageUrl || "",
          metaTitle: a.metaTitle || "",
          metaDescription: a.metaDescription || "",
          primaryKeyword: a.primaryKeyword || "",
          published: Boolean(a.published),
          publishedAtLocal:
            utcIsoToZonedDatetimeLocalInput(a.publishedAt, DEFAULT_SCHEDULE_TIME_ZONE) ||
            nowZonedDatetimeLocalInput(DEFAULT_SCHEDULE_TIME_ZONE),
          source: a.source,
          url: a.url,
        });
        setSlugTouched(true);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/articles", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeArticleId, navigate]);

  function openCreate() {
    setSlugTouched(false);
    setShowHtmlSource(false);
    setEditor({ ...emptyEditor(), open: true, mode: "create" });
  }

  function openEdit(a: Article) {
    setSlugTouched(true);
    setShowHtmlSource(false);
    setEditor({
      open: true,
      mode: "edit",
      articleId: a._id,
      title: a.title,
      slug: a.slug,
      locale: a.locale || "en",
      bodyHtml: a.bodyHtml || "",
      excerpt: a.excerpt || "",
      coverImageUrl: a.coverImageUrl || "",
      metaTitle: a.metaTitle || "",
      metaDescription: a.metaDescription || "",
      primaryKeyword: a.primaryKeyword || "",
      published: Boolean(a.published),
      publishedAtLocal:
        utcIsoToZonedDatetimeLocalInput(a.publishedAt, DEFAULT_SCHEDULE_TIME_ZONE) ||
        nowZonedDatetimeLocalInput(DEFAULT_SCHEDULE_TIME_ZONE),
      source: a.source,
      url: a.url,
    });
    navigate(`/articles/${a._id}`, { replace: true });
  }

  function closeEditor() {
    setShowHtmlSource(false);
    setEditor(emptyEditor());
    if (routeArticleId) navigate("/articles", { replace: true });
  }

  async function handleCoverFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setCoverUploading(true);
    try {
      const imageUrl = await uploadArticleCoverImage(file);
      setEditor((prev) => ({ ...prev, coverImageUrl: imageUrl }));
      toast.success("Cover image uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Cover upload failed");
    } finally {
      setCoverUploading(false);
    }
  }

  async function saveEditor() {
    if (!editor.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!editor.slug.trim()) {
      toast.error("Slug is required");
      return;
    }
    if (!editor.bodyHtml.trim()) {
      toast.error("Body is required");
      return;
    }

    const publishedAtIso = editor.publishedAtLocal.trim()
      ? zonedDatetimeLocalInputToUtcIso(editor.publishedAtLocal, DEFAULT_SCHEDULE_TIME_ZONE)
      : null;

    if (editor.publishedAtLocal.trim() && !publishedAtIso) {
      toast.error("Invalid publish date/time");
      return;
    }

    const payload = {
      title: editor.title.trim(),
      slug: editor.slug.trim(),
      locale: editor.locale.trim() || "en",
      bodyHtml: editor.bodyHtml,
      excerpt: editor.excerpt.trim() || undefined,
      coverImageUrl: editor.coverImageUrl.trim() || null,
      metaTitle: editor.metaTitle.trim() || undefined,
      metaDescription: editor.metaDescription.trim() || undefined,
      primaryKeyword: editor.primaryKeyword.trim() || null,
      published: editor.published,
      publishedAt: publishedAtIso ?? null,
    };

    try {
      setSaving(true);
      if (editor.mode === "create") {
        await articlesApi.create(payload);
        toast.success("Article created");
      } else if (editor.articleId) {
        await articlesApi.update(editor.articleId, payload);
        toast.success("Article updated");
      }
      closeEditor();
      await load({ soft: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await articlesApi.remove(deleteTarget._id);
      toast.success("Article deleted");
      setDeleteTarget(null);
      await load({ soft: true });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Articles</h1>
          <p className="text-sm text-muted-foreground">
            Manage the public blog. Posts can come from Brand Pilot or be written here.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void load({ soft: true })} disabled={refreshing}>
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New article
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>Search by title or description</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 lg:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Search articles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select
            value={publishedFilter}
            onValueChange={(v) => setPublishedFilter(v as "all" | "true" | "false")}
          >
            <SelectTrigger className="w-full lg:w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="true">Published</SelectItem>
              <SelectItem value="false">Draft / unpublished</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={sourceFilter}
            onValueChange={(v) => setSourceFilter(v as "all" | "admin" | "brand_pilot")}
          >
            <SelectTrigger className="w-full lg:w-44">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="brand_pilot">Brand Pilot</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {error ? (
        <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      ) : null}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-2/3" />
            </div>
          ) : items.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">No articles found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a._id}>
                    <TableCell>
                      <div className="font-medium">{a.title}</div>
                      <div className="text-xs text-muted-foreground">/{a.slug}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={a.published ? "default" : "secondary"}>
                        {a.published ? "Published" : "Unpublished"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {a.source === "brand_pilot" ? "Brand Pilot" : "Admin"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatAdminBusinessEt(a.publishedAt)}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {a.url && a.published ? (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={a.url} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      ) : null}
                      <Button variant="outline" size="sm" onClick={() => openEdit(a)}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(a)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 ? (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page} of {totalPages} · {total} total
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <Sheet open={editor.open} onOpenChange={(open) => (!open ? closeEditor() : undefined)}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-hidden border-l border-border p-0 sm:max-w-3xl"
        >
          <SheetHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
            <SheetTitle className="text-lg font-semibold">
              {editor.mode === "create" ? "New article" : "Edit article"}
            </SheetTitle>
            <SheetDescription>
              {editor.source === "brand_pilot"
                ? "Originally published via Brand Pilot — edits here update the live blog post."
                : "Published posts appear at /blog on the student site."}
            </SheetDescription>
          </SheetHeader>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div className="space-y-2">
              <Label htmlFor="article-title">Title</Label>
              <Input
                id="article-title"
                value={editor.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setEditor((prev) => ({
                    ...prev,
                    title,
                    slug: slugTouched ? prev.slug : slugify(title),
                    metaTitle: prev.metaTitle || title,
                  }));
                }}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="article-slug">Slug</Label>
                <Input
                  id="article-slug"
                  value={editor.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setEditor((prev) => ({ ...prev, slug: slugify(e.target.value) }));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="article-locale">Locale</Label>
                <Input
                  id="article-locale"
                  value={editor.locale}
                  onChange={(e) => setEditor((prev) => ({ ...prev, locale: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Body</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setShowHtmlSource((v) => !v)}
                >
                  {showHtmlSource ? "Visual editor" : "Paste HTML"}
                </Button>
              </div>
              {showHtmlSource ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    Paste the full HTML body here (tags like {"<p>"}, {"<h2>"}). This saves as
                    real HTML — not escaped text.
                  </p>
                  <Textarea
                    rows={16}
                    className="font-mono text-xs"
                    value={editor.bodyHtml}
                    onChange={(e) =>
                      setEditor((prev) => ({ ...prev, bodyHtml: e.target.value }))
                    }
                    placeholder='<p>Your article HTML…</p>'
                  />
                </>
              ) : (
                <RichTextEditor
                  value={editor.bodyHtml}
                  onChange={(bodyHtml) => setEditor((prev) => ({ ...prev, bodyHtml }))}
                  minHeight={280}
                  enableImageUrl
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="article-excerpt">Excerpt</Label>
              <Textarea
                id="article-excerpt"
                rows={2}
                value={editor.excerpt}
                onChange={(e) => setEditor((prev) => ({ ...prev, excerpt: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Cover image</Label>
              <p className="text-xs text-muted-foreground">
                Upload JPEG/PNG/WebP (max 5 MB), or paste a public image URL.
              </p>
              {editor.coverImageUrl.trim() ? (
                <div className="relative overflow-hidden rounded-md border border-border bg-muted/20">
                  <img
                    src={resolveNavImagePreviewUrl(editor.coverImageUrl)}
                    alt=""
                    className="h-40 w-full object-cover"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="absolute right-2 top-2 h-8 w-8"
                    onClick={() => setEditor((prev) => ({ ...prev, coverImageUrl: "" }))}
                    disabled={coverUploading}
                    aria-label="Remove cover image"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={coverFileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => void handleCoverFileChange(e)}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={coverUploading}
                  onClick={() => coverFileInputRef.current?.click()}
                >
                  {coverUploading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  {coverUploading ? "Uploading…" : "Upload image"}
                </Button>
              </div>
              <Input
                id="article-cover"
                placeholder="Or paste https://…"
                value={editor.coverImageUrl}
                onChange={(e) => setEditor((prev) => ({ ...prev, coverImageUrl: e.target.value }))}
                disabled={coverUploading}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="article-meta-title">SEO title</Label>
                <Input
                  id="article-meta-title"
                  value={editor.metaTitle}
                  onChange={(e) => setEditor((prev) => ({ ...prev, metaTitle: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="article-keyword">Primary keyword</Label>
                <Input
                  id="article-keyword"
                  value={editor.primaryKeyword}
                  onChange={(e) =>
                    setEditor((prev) => ({ ...prev, primaryKeyword: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="article-meta-desc">SEO description</Label>
              <Textarea
                id="article-meta-desc"
                rows={2}
                value={editor.metaDescription}
                onChange={(e) =>
                  setEditor((prev) => ({ ...prev, metaDescription: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="article-published-at">Publish date & time (EST)</Label>
              <Input
                id="article-published-at"
                type="datetime-local"
                value={editor.publishedAtLocal}
                onChange={(e) =>
                  setEditor((prev) => ({ ...prev, publishedAtLocal: e.target.value }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Shown on the public blog. Entered as US Eastern (EST).
              </p>
            </div>

            <div className="flex items-center gap-3 rounded-md border px-3 py-2">
              <Switch
                checked={editor.published}
                onCheckedChange={(published) => setEditor((prev) => ({ ...prev, published }))}
                id="article-published"
              />
              <Label htmlFor="article-published">Published on public blog</Label>
            </div>
          </div>

          <SheetFooter className="shrink-0 flex-row justify-end gap-2 border-t border-border px-6 py-4 sm:space-x-0">
            <Button variant="outline" onClick={closeEditor} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void saveEditor()} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => (!o ? setDeleteTarget(null) : undefined)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete article?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.title}” will be removed from the public blog. This soft-deletes the
              record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmDelete()}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
